<script setup>
import { ref, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useSalesTeamsStore } from 'dashboard/stores/salesTeams';
import { useAlert } from 'dashboard/composables';

const store = useStore();
const salesTeamsStore = useSalesTeamsStore();

const isOpen = ref(false);

const agents = ref([]);

function open() {
  isOpen.value = true;
  salesTeamsStore.fetch();
  store.dispatch('agents/get').then(() => {
    agents.value = store.getters['agents/getAgents'] ?? [];
  });
}

function close() {
  isOpen.value = false;
}

async function handleSetManager(teamId, managerId) {
  try {
    await salesTeamsStore.setManager(teamId, managerId || null);
    useAlert('Gerente salvo com sucesso.');
  } catch {
    useAlert('Erro ao salvar gerente.', { variant: 'error' });
  }
}

defineExpose({ open });
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @keydown.esc="close"
    >
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="close" />

      <div class="relative z-10 w-full max-w-lg flex flex-col rounded-2xl bg-n-surface-1 border border-n-weak shadow-xl max-h-[80vh]">
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak shrink-0">
          <div>
            <h2 class="text-base font-semibold text-n-slate-12">
              {{ $t('SALES.SALES_TEAM.TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-9 mt-0.5">
              {{ $t('SALES.SALES_TEAM.DESCRIPTION') }}
            </p>
          </div>
          <button
            class="p-1.5 rounded-lg text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
            @click="close"
          >
            <span class="i-lucide-x text-lg" />
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto px-4 py-4">
          <div v-if="salesTeamsStore.isFetching" class="flex justify-center py-8">
            <span class="i-lucide-loader-circle animate-spin text-2xl text-n-slate-9" />
          </div>

          <div v-else-if="!salesTeamsStore.teams.length" class="flex flex-col items-center gap-2 py-8 text-n-slate-9">
            <span class="i-lucide-users text-3xl" />
            <p class="text-sm">{{ $t('SALES.SALES_TEAM.NO_TEAMS') }}</p>
          </div>

          <div v-else class="flex flex-col gap-3">
            <div
              v-for="team in salesTeamsStore.teams"
              :key="team.id"
              class="rounded-xl border border-n-strong bg-n-alpha-3 px-4 py-3 flex items-center gap-3"
            >
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-n-slate-12 truncate">{{ team.name }}</p>
                <p class="text-xs text-n-slate-9 mt-0.5">
                  {{ team.member_count }} {{ $t('SALES.SALES_TEAM.MEMBERS') }}
                </p>
              </div>
              <div class="shrink-0 w-48">
                <select
                  class="w-full text-xs rounded-lg border border-n-strong bg-n-surface-1 text-n-slate-12 px-2 py-1.5 focus:outline-none focus:border-n-brand"
                  :value="team.manager_id ?? ''"
                  @change="handleSetManager(team.id, $event.target.value || null)"
                >
                  <option value="">{{ $t('SALES.SALES_TEAM.NO_MANAGER') }}</option>
                  <option
                    v-for="agent in agents"
                    :key="agent.id"
                    :value="agent.id"
                  >
                    {{ agent.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
